import Joi from 'joi';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import WithOnlyOnDesktop from '../onlyOnDesktop/withOnlyOnDesktop';
import InPageNotification from './InPageNotification';
import infoIcon from '../../assets/images/info-icon-gray.svg';
import utils from '../../utils/utils';
import { ERROR_CODES } from '../../constants';
import { showNotification } from '../../redux/notification';
import { setPreventNavigation, setLeaveConfirmationCopy } from '../../redux/navigate';
import Notification from '../notification/Notification';
import MESSAGE_MAP from '../../constants/AgencyProfessionalMessagesMap';

import './ProfessionalsEditEmployee.scss';

const { AGENCY_PROFESSIONAL_ERROR_CODES } = ERROR_CODES;

const defaultFormValues = {
	email: '',
	phone_number: ''
};

const validateForm = (formData) => {
	const professionalSchema = Joi.object({
		email: Joi.string()
			.trim()
			.max(129)
			.email({ tlds: { allow: false }, ignoreLength: true })
			.required()
			.messages({
				'string.base': 'Email is required',
				'string.empty': 'Email is required',
				'any.required': 'Email is required',
				'string.email': 'Email format is invalid',
				'string.max': 'Email can have a maximum length of 129 characters'
			}),
		phone_number: Joi.string()
			.length(12)
			.pattern(/^[0-9-]+$/)
			.messages({
				'string.base': 'Cell Phone number is required',
				'string.empty': 'Cell Phone number is required',
				'any.required': 'Cell Phone number is required',
				'string.pattern.base': 'Cell Phone number is not valid',
				'string.length': 'Phone number should be 10 digits long'
			})
			.required()
	});

	const { error } = professionalSchema.validate(formData, { abortEarly: false });
	const formErrors = error?.details.reduce((acc, curr) => {
		acc[curr.context.key] = curr.message;
		return acc;
	}, {});

	return formErrors;
};

const ProfessionalsAddEmployeeContactInfo = ({
	isSaving,
	submitForm,
	handleNavigateToNextStep,
	errorCodes = AGENCY_PROFESSIONAL_ERROR_CODES,
	messageMap = MESSAGE_MAP,
	bannerIcon = infoIcon,
	bannerClassName = '',
	containerClassName = '',
	bannerErrorCodes = [errorCodes.CONTACT_US],
	existingMethodName = 'addExistingProfessionalToAgency',
	existingProfessionalMessage = `
		This professional already exists.
		A message has been sent to their mobile app asking for their approval to join your agency.
	`,
	confirmLeaveWhenDirty = false,
	leaveConfirmationTitle = 'Leave without adding professional?',
	leaveConfirmationMessage = 'This professional will not be added if you leave now.'
}) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const [formData, setFormData] = useState(defaultFormValues);
	const [formErrors, setFormErrors] = useState(defaultFormValues);
	const [showErrorBanner, setShowErrorBanner] = useState(null);
	const [errorBannerMessage, setErrorBannerMessage] = useState('');
	const [errorBannerCode, setErrorBannerCode] = useState('');
	const [showInPageNotification, setShowInPageNotification] = useState(false);
	const [inPageNotificationData, setInPageNotificationData] = useState({});
	const ACCEPTED_ERRORS_CODES = [errorCodes.ALREADY_EXISTS, errorCodes.CONTACT_US];

	const isDirty = Boolean(formData.email || formData.phone_number);

	useEffect(() => {
		if (!confirmLeaveWhenDirty) return undefined;
		dispatch(
			setLeaveConfirmationCopy({
				title: leaveConfirmationTitle,
				message: leaveConfirmationMessage,
				confirmLabel: 'Leave anyway',
				cancelLabel: 'Stay on page'
			})
		);
		dispatch(setPreventNavigation(isDirty));
		return () => {
			dispatch(setPreventNavigation(false));
			dispatch(setLeaveConfirmationCopy(null));
		};
	}, [confirmLeaveWhenDirty, isDirty, leaveConfirmationTitle, leaveConfirmationMessage, dispatch]);

	useEffect(() => {
		if (location.state?.notification) {
			setShowInPageNotification(true);
			setInPageNotificationData({
				type: location.state?.notification.type,
				message: location.state?.notification.message
			});
		}
	}, [location.state]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const updatePhoneNumber = (e) => {
		let { value } = e.target;
		const { name } = e.target;
		if (utils.invalidPhoneCharactersRegex.test(value)) {
			value = value.replace(utils.invalidPhoneCharactersRegex, '');
		}
		if (value.length > 12) {
			e.preventDefault();
		} else {
			setFormData({ ...formData, [name]: utils.formatPhoneNumber(value) });
		}
	};

	const closeInPageNotification = () => {
		setShowInPageNotification(false);
		setInPageNotificationData({});
	};

	const handleSubmit = async (e) => {
		setShowErrorBanner(false);
		closeInPageNotification();
		e.preventDefault();

		const errors = validateForm(formData);
		setFormErrors((prevErrors) => ({
			...prevErrors,
			...defaultFormValues,
			...errors
		}));

		const trimmedFormData = Object.fromEntries(Object.entries(formData).map(([key, value]) => [key, value?.trim()]));

		if (!errors) {
			dispatch(setPreventNavigation(false));
			try {
				const result = await submitForm({ body: trimmedFormData }).unwrap();
				if (result && result.isSuccessful && result.method === existingMethodName) {
					setShowInPageNotification(true);
					setInPageNotificationData({
						type: 'success',
						message: existingProfessionalMessage
					});
				} else if (result) {
					handleNavigateToNextStep({
						data: trimmedFormData,
						notification: {
							type: 'info',
							message: messageMap[errorCodes.SUCCESS]
						}
					});
				}
			} catch (error) {
				const currentError = error?.data?.errors?.[0];
				if (!currentError) {
					dispatch(showNotification('error', 'Something went wrong. Please try again.'));
					return;
				}

				if (currentError.field) {
					setFormErrors((prevErrors) => ({
						...prevErrors,
						...defaultFormValues,
						[currentError.field]: currentError.message
					}));
				}

				if (bannerErrorCodes.includes(currentError.code)) {
					setErrorBannerMessage(messageMap[currentError.code]);
					setErrorBannerCode(currentError.code);
					setShowErrorBanner(true);
				} else if (ACCEPTED_ERRORS_CODES.includes(currentError.code)) {
					setShowInPageNotification(true);
					setInPageNotificationData({ type: 'info', message: messageMap[currentError.code] });
				} else {
					dispatch(showNotification('error', currentError.message));
				}
			}
		}
	};

	return (
		<WithOnlyOnDesktop>
			{showInPageNotification && <InPageNotification data={inPageNotificationData} onClose={closeInPageNotification} />}
			<div className={classNames('employee-details__wrapper', containerClassName)}>
				<Notification />
				<Form onSubmit={handleSubmit}>
					<div className="employee-details">
						<h2>Contact Information</h2>
						<Form.Group controlId="phone_number" className="form-input-group">
							<Form.Label>Cell phone</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="phone_number"
								placeholder="555-555-5555"
								onChange={updatePhoneNumber}
								autoComplete="off"
								value={formData.phone_number}
								isInvalid={formErrors.phone_number}
							/>
							<Form.Control.Feedback type="invalid">{formErrors.phone_number}</Form.Control.Feedback>
						</Form.Group>
						<Form.Group controlId="email" className="form-input-group">
							<Form.Label>Email</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="email"
								placeholder="email@address.com"
								onChange={handleChange}
								value={formData.email}
								isInvalid={formErrors.email}
							/>
							<Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
						</Form.Group>
						{showErrorBanner && (
							<div
								className={classNames('notification-banner-error', bannerClassName, {
									'already-exists': errorBannerCode === errorCodes.ALREADY_EXISTS
								})}
							>
								<img className="notification-banner-icon" alt="Warning icon" src={bannerIcon} />
								<span className="notification-banner-message">{errorBannerMessage}</span>
							</div>
						)}
					</div>
				</Form>
				<div className="form-actions">
					<button
						type="submit"
						className="form-actions__button primary-button"
						onClick={handleSubmit}
						disabled={isSaving}
					>
						Continue
					</button>
				</div>
			</div>
		</WithOnlyOnDesktop>
	);
};

export default ProfessionalsAddEmployeeContactInfo;
